import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api/client';

/**
 * Rank 12 — leaderboards & gamification.
 *
 * Backed by the score service (`services/score/handler.py`), which already
 * persists per-user SOC triage scores + scenario/objective completions:
 *   GET /leaderboard → getLeaderboard (tenant-wide ranked table + badge catalog)
 *   GET /me/stats    → getUserStats   (the caller's aggregated stats + badges)
 *
 * Points are difficulty-weighted (hard scenarios are worth more) and badges are
 * derived from each user's stats by a pure function in the backend
 * (services/score/leaderboard.py). This hook just reads the ranking.
 */

export interface LeaderboardBadge {
  id: string;
  label: string;
  description: string;
  /** lucide icon name the showcase renders. */
  icon: string;
}

export interface LeaderboardEntry {
  rank: number;
  user_id: number;
  name: string;
  points: number;
  scenarios_completed: number;
  /** 0..1 triage accuracy. */
  accuracy: number;
  soc_score: number;
  /** 0..1 attack detection rate. */
  detection_rate: number;
  badges: ReadonlyArray<LeaderboardBadge>;
}

export interface LeaderboardResponse {
  tenant_id: number;
  entries: ReadonlyArray<LeaderboardEntry>;
  count: number;
  /** Every derivable badge, for the "locked" showcase of not-yet-earned ones. */
  badge_catalog: ReadonlyArray<LeaderboardBadge>;
}

export interface UserStatsResponse {
  user_id: number;
  name: string;
  points: number;
  soc_score: number;
  classified: number;
  correct: number;
  tp_caught: number;
  accuracy: number;
  scenarios_completed: number;
  detection_rate: number;
  badges: ReadonlyArray<LeaderboardBadge>;
}

export const leaderboardKeys = {
  board: () => ['leaderboard'] as const,
  me: () => ['leaderboard', 'me', 'stats'] as const,
};

/** Tenant-wide ranked leaderboard + badge catalog. */
export function useLeaderboard() {
  return useQuery<LeaderboardResponse>({
    queryKey: leaderboardKeys.board(),
    staleTime: 30_000,
    queryFn: async ({ signal }) =>
      apiFetch<LeaderboardResponse>('/leaderboard', { method: 'GET', signal }),
  });
}

/** The calling user's aggregated stats + earned badges. */
export function useMyStats(enabled = true) {
  return useQuery<UserStatsResponse>({
    queryKey: leaderboardKeys.me(),
    enabled,
    staleTime: 30_000,
    queryFn: async ({ signal }) =>
      apiFetch<UserStatsResponse>('/me/stats', { method: 'GET', signal }),
  });
}
